import { apiClient } from '../client'
import { localTimeZone, timeZoneParam } from '../../utils/localTimeZone'
import { ENDPOINTS } from '../utils/endpoints'
import { DEFAULT_CARD_GEN_PROMPT } from '../../constants/prompts'
import { auth, env, session, storage, telemetry } from '../../platform'

// Same base-URL source as the Axios client (packages/core/api/client/index.js).
// Read lazily, not at module scope: the port is configured by the client after
// this module is imported.
const streamBaseUrl = () => env.apiUrl

// Stall detector: abort the stream if NO bytes (data or heartbeat) arrive for this long
const STREAM_STALL_MS = 30000

/**
 * Report a streaming failure to Sentry.
 * The fetch-based SSE path bypasses the Axios interceptors, so this is the
 * explicit capture point. No-op when Sentry is not initialized — same guard
 * as captureApiError in api/client/index.js.
 */
const captureStreamIssue = (errorOrMessage, tags = {}) => {
  const options = { tags: { api_error: true, sse_stream: true, ...tags } }
  if (errorOrMessage instanceof Error) {
    telemetry.captureException(errorOrMessage, options)
  } else {
    telemetry.captureMessage(errorOrMessage, options)
  }
}

/**
 * Replicates the Axios request interceptor's token logic
 * (api/client/index.js lines ~27-41) for the fetch-based SSE path:
 * getIdToken(false) → Authorization header + stored-token sync,
 * with a fallback to the cached stored token on failure.
 */
const buildStreamAuthHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' }
  try {
    const firebaseUser = auth.currentUser()
    if (firebaseUser) {
      // getIdToken(false) returns the cached token and silently refreshes
      // only when it is close to expiry (Firebase SDK internal threshold).
      const token = await auth.getIdToken(false)
      headers.Authorization = `Bearer ${token}`
      // Keep stored token in sync for any code that still reads it directly
      storage.set('firebase_token', token)
    }
  } catch {
    // If we can't get a token, fall back to the cached stored value
    const cached = storage.get('firebase_token')
    if (cached) {
      headers.Authorization = `Bearer ${cached}`
    }
  }
  return headers
}

/**
 * Parse a single SSE frame into { event, data } or null.
 * Heartbeat/comment lines (starting with ':') are ignored.
 * Multi-line data fields are joined with '\n' per the SSE spec.
 */
const parseSseFrame = (frame) => {
  let event = 'message'
  const dataLines = []
  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '')
    if (!line || line.startsWith(':')) continue // heartbeat / comment
    if (line.startsWith('event:')) {
      event = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart())
    }
  }
  if (dataLines.length === 0) return null
  return { event, data: dataLines.join('\n') }
}

/**
 * Study Cards Service
 * Handles AI-powered study card generation and manual CRUD
 */
export const cardsService = {
  /**
   * Generate study cards from text using AI
   * @param {string} sampleText - Text to generate cards from
   * @param {number} sampleNumber - Number of cards to generate
   * @param {string} prompt - Optional custom prompt for generation
   * @returns {Promise<Array>} Array of generated study cards
   */
  async generate(sampleText, sampleNumber, prompt = null, config = {}) {
    const generationPrompt = prompt || process.env.REACT_APP_CARD_GENERATION_PROMPT || DEFAULT_CARD_GEN_PROMPT

    const { data } = await apiClient.post(
      ENDPOINTS.studyCards.generate,
      {
        prompt: generationPrompt,
        sampleText,
        sampleNumber
      },
      config
    )

    return data
  },

  /**
   * Onboarding's explicit AI fallback (FR-032, FR-033).
   *
   * This is the *same* `/card/generate` endpoint with no onboarding semantics
   * added: it returns no activation block and writes no journey state. It
   * exists as a named adapter so the invariant is impossible to misread — the
   * only thing that activates onboarding is a verified curated deck fork
   * (ADR-006).
   *
   * Two rules bind every caller:
   * - Invoke it only from an explicit user action. It must never fire when the
   *   empty state opens or when a curated-deck load finishes with no results.
   * - Never treat a successful generation as onboarding completion. It stays
   *   incomplete, and once measurement ships it counts as fallback usage.
   *
   * @param {string} topicContext - Confirmed primary topic used as the
   *   user-visible source text for generation
   * @param {number} [cardCount=10] - Number of cards to request
   * @param {string|null} [prompt=null] - Optional custom generation prompt
   * @returns {Promise<Array>} Generated cards — never a journey state
   */
  async generateOnboardingFallback(topicContext, cardCount = 10, prompt = null) {
    // ONB-014: the empty state renders its own translated fallback error with a
    // retry, so the client's generic error toast is opted out of here — and only
    // here. Every other `generate` caller keeps it.
    return cardsService.generate(topicContext, cardCount, prompt, { suppressErrorToast: true })
  },

  /**
   * Generate study cards via the SSE streaming endpoint.
   * Cards are delivered incrementally through callbacks; terminal events
   * are `done` (success) and `error` (failure). Uses fetch (not Axios)
   * because Axios cannot consume ReadableStream bodies in the browser.
   *
   * @param {string} sampleText - Text to generate cards from (max 20000 chars)
   * @param {'auto'|number} countMode - 'auto' sends sampleNumber: null (adaptive
   *   count decided by the backend); an integer 1-50 requests a fixed count.
   * @param {string|null} prompt - Optional custom prompt for generation
   * @param {Object} handlers
   * @param {string[]} [handlers.excludeTitles] - Titles of already-generated cards
   *   to exclude ("Generate more" flow). Max 50, each ≤200 chars.
   * @param {(e: {index: number, total: number, card: {title: string, content: string}}) => void} handlers.onCard
   * @param {(e: {total_cards: number, elapsed_ms: number, mode: 'auto'|'fixed', cap: number, truncated: boolean}) => void} handlers.onDone
   * @param {(e: {code: string, status?: number, detail?: any}) => void} handlers.onError
   * @param {AbortSignal} [handlers.signal] - Abort signal for user cancellation
   */
  async generateStream(sampleText, countMode, prompt = null, { excludeTitles, onCard, onDone, onError, signal } = {}) {
    const generationPrompt = prompt || process.env.REACT_APP_CARD_GENERATION_PROMPT || DEFAULT_CARD_GEN_PROMPT
    const headers = await buildStreamAuthHeaders()

    const body = {
      prompt: generationPrompt,
      sampleText,
      sampleNumber: countMode === 'auto' ? null : countMode
    }
    if (Array.isArray(excludeTitles) && excludeTitles.length > 0) {
      body.excludeTitles = excludeTitles
    }

    let response
    try {
      response = await fetch(`${streamBaseUrl()}${ENDPOINTS.studyCards.generateStream}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
        signal
      })
    } catch (err) {
      // User cancellation is not an error
      if (err?.name === 'AbortError') return
      captureStreamIssue(err, { sse_error_code: 'NETWORK_ERROR' })
      onError?.({ code: 'NETWORK_ERROR' })
      return
    }

    // Pre-stream failures arrive as plain HTTP statuses with FastAPI JSON bodies
    if (!response.ok) {
      let detail
      try {
        const body = await response.json()
        detail = body?.detail
      } catch {
        detail = undefined
      }
      if (response.status === 401) {
        // Same session-expiry mechanism as the Axios response interceptor,
        // minus the redirect: this path only announces, and that difference is
        // preserved rather than unified, because unifying it would change
        // behaviour.
        storage.remove('firebase_token')
        storage.remove('firebase_user')
        session.onUnauthorized({ redirect: false })
      }
      if (response.status >= 500) {
        captureStreamIssue(`SSE pre-stream HTTP ${response.status}`, {
          sse_error_code: `HTTP_${response.status}`,
          status_code: response.status
        })
      }
      onError?.({ code: `HTTP_${response.status}`, status: response.status, detail })
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    let stallTimer = null
    let terminated = false

    const clearStallTimer = () => {
      if (stallTimer) {
        clearTimeout(stallTimer)
        stallTimer = null
      }
    }

    // Reset on EVERY reader chunk (heartbeats included) — fires only when the
    // connection goes silent for STREAM_STALL_MS with no terminal event.
    const resetStallTimer = () => {
      clearStallTimer()
      stallTimer = setTimeout(() => {
        terminated = true
        captureStreamIssue('SSE stream stalled (no bytes for 30s)', { sse_error_code: 'STREAM_STALLED' })
        onError?.({ code: 'STREAM_STALLED' })
        reader.cancel().catch(() => {})
      }, STREAM_STALL_MS)
    }

    const handleAbort = () => {
      clearStallTimer()
      reader.cancel().catch(() => {})
    }
    signal?.addEventListener('abort', handleAbort)

    // Dispatch one parsed frame. Returns true when the event was terminal.
    const dispatchFrame = (frame) => {
      const parsed = parseSseFrame(frame)
      if (!parsed) return false
      let data
      try {
        data = JSON.parse(parsed.data)
      } catch {
        // Unparseable frame: breadcrumb + skip — never crash the stream
        {
          telemetry.addBreadcrumb({
            category: 'sse',
            message: 'Skipped unparseable SSE frame',
            level: 'warning',
            data: { event: parsed.event }
          })
        }
        return false
      }
      if (parsed.event === 'card') {
        onCard?.(data)
        return false
      }
      if (parsed.event === 'done') {
        terminated = true
        onDone?.(data)
        return true
      }
      if (parsed.event === 'error') {
        terminated = true
        captureStreamIssue(`SSE error event: ${data?.code}`, { sse_error_code: data?.code || 'UNKNOWN' })
        onError?.(data)
        return true
      }
      return false
    }

    try {
      resetStallTimer()
      for (;;) {
        const { done, value } = await reader.read()
        if (terminated) return
        if (done) break
        resetStallTimer()
        buffer += decoder.decode(value, { stream: true })
        let separatorIndex
        while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, separatorIndex)
          buffer = buffer.slice(separatorIndex + 2)
          if (dispatchFrame(frame)) {
            clearStallTimer()
            reader.cancel().catch(() => {})
            return
          }
        }
      }
      // Stream closed by the server without a terminal event — treat as a stall
      if (!terminated && !signal?.aborted) {
        captureStreamIssue('SSE stream closed without terminal event', { sse_error_code: 'STREAM_STALLED' })
        onError?.({ code: 'STREAM_STALLED' })
      }
    } catch (err) {
      if (terminated || signal?.aborted || err?.name === 'AbortError') return
      captureStreamIssue(err, { sse_error_code: 'NETWORK_ERROR' })
      onError?.({ code: 'NETWORK_ERROR' })
    } finally {
      clearStallTimer()
      signal?.removeEventListener('abort', handleAbort)
    }
  },

  /**
   * Generate flashcards from a document (Plus+ only).
   * @param {string} bookId - Book ID
   * @param {number[]} [sections] - Section indexes from booksService.getSections (BOOK-001 D4);
   *   absent = the whole document. With sections every card carries `source_section`.
   * @returns {Promise<{cards: Array<{title: string, content: string, source_section?: object}>, source_book_id?: string, source_book_title?: string}>}
   */
  async generateFromBook(bookId, sections = null) {
    const body = { book_id: bookId }
    if (Array.isArray(sections) && sections.length) body.sections = sections
    const { data } = await apiClient.post(ENDPOINTS.studyCards.generateFromBook, body, { timeout: 120000 })
    return data
  },

  /**
   * Run AI deck analysis: duplicates, gaps, rewrite suggestions (Pro only)
   * @param {string} deckId - Deck ID
   * @returns {Promise<{duplicates: Array, gaps: Array, rewrite_suggestions: Array}>}
   */
  async analyzeDeck(deckId) {
    const { data } = await apiClient.post(
      ENDPOINTS.studyCards.analyzeDeck,
      {
        deck_id: deckId
      },
      { timeout: 120000 }
    )
    return data
  },

  /**
   * Get paginated study cards
   * @param {number} skip - Offset for pagination
   * @param {number} limit - Number of cards to fetch
   * @param {string[]} tags - Optional tag filters (OR logic on backend)
   * @param {string} search - Optional search query (server-side, title/content/tags/deck)
   * @param {boolean} markedOnly - Restrict to cards the user has marked (ADR-010)
   * @param {'marked'|'struggling'|null} group - A system group (STUDY-001); struggling
   *   cards come back with `last_grade` / `last_graded_at`
   * @param {boolean} untagged - Cards whose `tags` is absent or empty (PRD D15, FR-009);
   *   with `tags` the server returns the union
   */
  async getAll(skip = 0, limit = 50, tags = [], search = '', markedOnly = false, group = null, untagged = false) {
    const params = new URLSearchParams({ skip, limit })
    tags.forEach((t) => params.append('tags', t))
    if (search) params.append('search', search)
    if (markedOnly) params.append('marked_only', 'true')
    if (group) params.append('group', group)
    if (untagged) params.append('untagged', 'true')
    const { data } = await apiClient.get(`${ENDPOINTS.studyCards.all}?${params}`)
    return data
  },

  /**
   * One verb over many cards (PRD FR-010, ADR-023). The server touches only
   * the caller's own active cards and ignores unknown ids; the mark branch
   * writes through the mark helper so `marked_at` keeps one owner (ADR-010).
   * @param {{ ids: string[], action: 'move'|'tag'|'untag'|'mark'|'unmark'|'delete',
   *   deckId?: string, tags?: string[] }} request
   * @returns {Promise<{updated: number}>}
   */
  async bulk({ ids, action, deckId, tags, until }) {
    const body = { ids, action }
    if (deckId !== undefined) body.deck_id = deckId
    if (tags !== undefined) body.tags = tags
    if (until !== undefined && until !== null) body.until = until instanceof Date ? until.toISOString() : until
    const { data } = await apiClient.post('/study-cards/bulk', body)
    return data
  },

  /**
   * Rest a card: out of play, schedule kept (SR-003, ADR-043). `until` is a
   * Date, or null for "until woken". The card's own hold only.
   */
  async rest(id, until = null) {
    const { data } = await apiClient.put(`/study-cards/${id}/rest`, { until: until ? until.toISOString() : null })
    return data
  },

  /** Wake a card: its own hold goes; a tag's or a deck's stays. */
  async wake(id) {
    const { data } = await apiClient.delete(`/study-cards/${id}/rest`)
    return data
  },

  /**
   * Rest a tag: every card carrying it leaves play (SR-003).
   * @returns {Promise<{tag: string, rest_until: string | null, cards: number}>}
   */
  async restTag(tag, until = null) {
    const { data } = await apiClient.put(`/study-cards/tags/${encodeURIComponent(tag)}/rest`, { until: until ? until.toISOString() : null })
    return data
  },

  /** Wake a tag; the woken cards land softly (SR-002). */
  async wakeTag(tag) {
    const { data } = await apiClient.delete(`/study-cards/tags/${encodeURIComponent(tag)}/rest`)
    return data
  },

  /**
   * The soft landing, asked for (SR-002): spread the overdue pile forward.
   * `perDay` below the deck's cap is "Lighten today".
   * @returns {Promise<{cards: number, days: number, decks: number}>}
   */
  async spread({ deckId, perDay, includeToday = false } = {}) {
    const body = { include_today: includeToday }
    if (deckId) body.deck_id = deckId
    if (perDay) body.per_day = perDay
    const { data } = await apiClient.post('/study-cards/spread', body)
    return data
  },

  /**
   * Rename a tag on every card (PRD FR-011). Renaming onto a tag that already
   * exists is a merge — the same call, no second endpoint.
   * @returns {Promise<{cards: number}>}
   */
  async renameTag(from, to) {
    const { data } = await apiClient.post('/study-cards/tags/rename', { from, to })
    return data
  },

  /**
   * Pull a tag from every card (PRD FR-011).
   * @returns {Promise<{cards: number}>}
   */
  async removeTag(tag) {
    const { data } = await apiClient.post('/study-cards/tags/remove', { tag })
    return data
  },

  /**
   * Due counts per local day for the coming days (STUDY-001). Starts tomorrow;
   * today's number is `summary.due_today` in getStatistics(), one owner.
   * @param {number} days
   * @returns {Promise<{days: Array<{date: string, due: number}>, total: number}>}
   */
  async getForecast(days = 7) {
    const params = new URLSearchParams({ days })
    const tz = localTimeZone()
    if (tz) params.append('tz', tz)
    const { data } = await apiClient.get(`/study-cards/forecast?${params}`)
    return data
  },

  /**
   * Every tag and the two system groups (marked, struggling) with
   * cards / decks / due / new (STUDY-001).
   * @returns {Promise<{system: Array, tags: Array}>}
   */
  async getGroups() {
    const { data } = await apiClient.get('/study-cards/groups')
    return data
  },

  /**
   * Get all tags used across the user's cards with their counts
   * @returns {Promise<Array<{tag: string, count: number}>>}
   */
  async getTags() {
    const { data } = await apiClient.get('/study-cards/tags')
    return data
  },

  /**
   * Create a manual study card
   */
  async create(cardData) {
    const { data } = await apiClient.post(ENDPOINTS.studyCards.create, cardData)
    return data
  },

  /**
   * One card by id.
   *
   * Every list endpoint returns cards, so the web has never needed this: it
   * always has the object in hand before it opens an editor. A phone does not
   * — a route is a URL, and `/study/card/<id>` carries an id and nothing else.
   *
   * @param {string} id - Card ID
   * @returns {Promise<Object>} The card
   */
  async getById(id) {
    const { data } = await apiClient.get(ENDPOINTS.studyCards.byId(id))
    return data
  },

  /**
   * Update an existing card
   */
  async update(id, updates) {
    const { data } = await apiClient.patch(ENDPOINTS.studyCards.update(id), updates)
    return data
  },

  /**
   * Delete a card
   */
  async delete(id) {
    const { data } = await apiClient.delete(ENDPOINTS.studyCards.delete(id))
    return data
  },

  /**
   * Flag a card the user wants to come back to.
   *
   * Deliberately NOT `update()`: that route is the one that can move a card's
   * SM-2 schedule, and the mark is a separate axis that must never travel on it
   * (ADR-010). The server refuses `marked_at` on PATCH for the same reason.
   * @param {string} id - Card ID
   * @returns {Promise<object>} The updated card, carrying its new `marked_at`
   */
  async mark(id) {
    const { data } = await apiClient.put(ENDPOINTS.studyCards.mark(id))
    return data
  },

  /**
   * Clear a card's mark. Nothing in the app clears a mark on the user's behalf,
   * so this is the only way one goes away.
   * @param {string} id - Card ID
   * @returns {Promise<object>} The updated card, with `marked_at` null
   */
  async unmark(id) {
    const { data } = await apiClient.delete(ENDPOINTS.studyCards.mark(id))
    return data
  },

  /**
   * Review a card with SM-2 grading
   * @param {string} id - Card ID
   * @param {string} grade - Grade: 'again', 'hard', 'good', or 'easy'
   * @param {string} [mode='study'] - Active session mode: 'study' or 'browse'.
   *   Only 'study' may grade/mutate the SM-2 schedule server-side; browse is
   *   rejected with 403 (defense-in-depth, D-06).
   * @param {object|null} [attempt=null] - the attempt behind the grade, shaped by
   *   `attemptPayload` (ADR-059). A practice showing travels as one and moves
   *   nothing on the server. Absent, the server records what it knows.
   */
  async review(id, grade, mode = 'study', attempt = null) {
    const url = `/study-cards/${id}/review?grade=${grade}&mode=${mode}`
    const { data } = attempt ? await apiClient.post(url, attempt) : await apiClient.post(url)
    return data
  },

  /**
   * The deck's study session: the day's selection, and the pool's tags.
   *
   * `tags` narrow the POOL the day's budgets draw from (TAG-001, ADR-044), so
   * a tag's session is SM-2's own selection for that tag's cards. `tags` on
   * the answer is the deck's tags with something waiting, unnarrowed, so the
   * learner can move between them.
   *
   * @param {string} deckId
   * @param {{ tags?: string[] }} [options]
   * @returns {Promise<{ items: Array<object>, total: number, tags: Array<{tag: string, new: number, due: number}> }>}
   */
  async getDueCards(deckId, { tags = [] } = {}) {
    const params = new URLSearchParams({ deck_id: deckId, due_only: 'true', limit: 500 })
    tags.forEach((t) => params.append('tags', t))
    const { data } = await apiClient.get(`${ENDPOINTS.studyCards.all}?${params}`)
    const items = data.cards || []
    return { items, total: data.total ?? items.length, tags: data.tags || [] }
  },

  /**
   * Get all cards for a deck regardless of due date (Browse mode)
   * @param {string} deckId - Deck ID to fetch all cards for
   * @returns {Promise<Array>} Array of every study card in the deck
   */
  async getAllCards(deckId) {
    const params = new URLSearchParams({ deck_id: deckId, due_only: 'false', limit: 500 })
    const { data } = await apiClient.get(`${ENDPOINTS.studyCards.all}?${params}`)
    return data.cards || []
  },

  /**
   * Get today's locked daily review session across all active decks.
   * The selection is sticky for the day on the backend (introduced_at stamp),
   * so the same cards reappear across sessions until graded.
   * @param {{ limit?: number, tags?: string[], group?: string }} [options] - STUDY-001:
   *   `limit` caps the session (due first), `tags` / `group` narrow the pool server-side.
   * @returns {Promise<Array>} Array of cards (new + due reviews) for today
   */
  async getDailyReviewCards({ limit, tags = [], group } = {}) {
    const params = new URLSearchParams()
    if (limit) params.append('limit', limit)
    tags.forEach((t) => params.append('tags', t))
    if (group) params.append('group', group)
    const query = params.toString()
    const { data } = await apiClient.get(`/study-cards/daily-review${query ? `?${query}` : ''}`)
    // The cards, and what the day said about them: how many rest, and whether a
    // pile was spread on arrival (SR-002). `items` is the name the web's session
    // already reads for a wrapped list. And the pool's tags (TAG-001), which
    // this object left behind when SR-002 rebuilt it, so the phone's daily
    // review listed no tags at all (MOB-118).
    return {
      items: data.cards || [],
      total: data.total ?? (data.cards || []).length,
      resting: data.resting ?? 0,
      spread: data.spread ?? null,
      tags: data.tags || []
    }
  },

  /**
   * Get study statistics
   */
  async getStatistics() {
    const { data } = await apiClient.get(`/study-cards/statistics${timeZoneParam()}`)
    return data
  }
}
